import { useEffect, useRef, useState } from "react";

import EditorFrame from "@/components/editors/EditorFrame";
import { VideoTypeTemplate as IVideo } from "@/states/page-editor/template-parts/video";
import { embedUrl } from "@/utils/video";

import IconPlay from "/public/icon/movie-play.svg";

interface Props {
  part: IVideo;
}

export default function VideoTemplate({ part }: Props) {
  return (
    <>
      {!part.deleted && (
        <div className="mt-5">
          <EditorFrame>
            <VideoPreviewer video={part} />
          </EditorFrame>
        </div>
      )}
    </>
  );
}

interface VideoPreviewerProps {
  video: IVideo;
  height?: number;
  width?: number;
}

function VideoPreviewer({ video, width, height }: VideoPreviewerProps) {
  if (video.url) {
    return <VideoUrlPreviewer url={video.url} height={height} width={width} />;
  }
  if (video.video) {
    if (
      typeof video.video === "object" &&
      Object.keys(video.video).length === 0
    )
      return <></>;
    return (
      <VideoFilePreviewer file={video.video} height={height} width={width} />
    );
  }
  return (
    <div className="aspect-video flex justify-center items-center w-full /*md:min-w-[560px] bg-theme-medium md:min-h-[315px]*/">
      <IconPlay className="w-[79px] h-[79px] fill-white" />
    </div>
  );
}

interface VideoUrlPreviewerProps {
  url: string;
  width?: number;
  height?: number;
}

function VideoUrlPreviewer({ url, width, height }: VideoUrlPreviewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.getAttribute("data-src")) {
      const src = iframeRef.current.getAttribute("data-src");
      if (src) {
        iframeRef.current.setAttribute("src", src);
      }
    }
  }, []);
  const src = embedUrl(url);
  return (
    <div>
      {src !== undefined ? (
        <iframe
          loading="lazy"
          data-src={src}
          ref={iframeRef}
          style={{ aspectRatio: "16 / 9", width: "100%", height: "auto" }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          {url && (
            <video
              width={width ?? undefined}
              height={height ?? undefined}
              src={url}
              controls
            />
          )}
        </>
      )}
    </div>
  );
}

interface VideoFilePreviewer {
  file: File;
  width?: number;
  height?: number;
}

function VideoFilePreviewer({ file, width, height }: VideoFilePreviewer) {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    const url = window.URL.createObjectURL(file);
    setSrc(url);
  }, [file]);

  return (
    <div>
      {src && (
        <video width={width ?? 560} height={height ?? 315} src={src} controls />
      )}
    </div>
  );
}
